import { useState, useCallback } from 'react'

export interface NotificationOptions {
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message: string
  duration?: number
  actions?: Array<{
    label: string
    action: () => void
    primary?: boolean
  }>
}

export interface NotificationState {
  id: string
  isVisible: boolean
  options: NotificationOptions
}

export const useNotification = () => {
  const [notifications, setNotifications] = useState<NotificationState[]>([])

  const showNotification = useCallback((options: NotificationOptions) => {
    const id = `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    const duration = options.duration || (options.type === 'error' ? 8000 : 5000)
    
    const notification: NotificationState = {
      id,
      isVisible: true,
      options
    }

    setNotifications(prev => [...prev, notification])

    // Auto-dismiss after duration
    if (duration > 0) {
      setTimeout(() => {
        hideNotification(id)
      }, duration)
    }

    return id
  }, [])

  const hideNotification = useCallback((id: string) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === id 
          ? { ...notification, isVisible: false }
          : notification
      )
    )

    // Remove from state after animation
    setTimeout(() => {
      setNotifications(prev => prev.filter(notification => notification.id !== id))
    }, 300)
  }, [])

  const hideAllNotifications = useCallback(() => {
    setNotifications(prev => 
      prev.map(notification => ({ ...notification, isVisible: false }))
    )

    setTimeout(() => {
      setNotifications([])
    }, 300)
  }, [])

  return {
    notifications,
    showNotification,
    hideNotification,
    hideAllNotifications
  }
} 